/**
 * useXP.js — CRM XP / Leaderboard hook
 *
 * Calculates XP for each contact based on:
 * - Role XP: crew contacts earn XP for jobs where they appear in calcSnapshot teams
 * - Engagement XP: all contacts earn XP for interactions
 * - Client Revenue XP: client contacts earn XP based on total revenue ($100 = 1 XP)
 *
 * Exports:
 * - useXP(): returns { getContactXP(contact), leaderboard }
 */

import { useMemo } from 'react';
import { useJobs } from './useJobs';
import { useContacts } from './useContacts';
import { useSettings } from './useSettings';
import { normalizeSnapshot, getAllEarnings } from '../lib/calcEngine';

const XP_TIERS = {
  bronze: { min: 0, max: 99, label: 'Bronze', color: '#B06820' },
  silver: { min: 100, max: 499, label: 'Silver', color: '#8B8B8B' },
  gold: { min: 500, max: 1999, label: 'Gold', color: '#C9A500' },
  platinum: { min: 2000, max: Infinity, label: 'Platinum', color: '#6B7280' },
};

function getTier(totalXP) {
  if (totalXP >= XP_TIERS.platinum.min) return 'platinum';
  if (totalXP >= XP_TIERS.gold.min) return 'gold';
  if (totalXP >= XP_TIERS.silver.min) return 'silver';
  return 'bronze';
}

export function useXP() {
  const { data: allJobs = [] } = useJobs();
  const { data: allContacts = [] } = useContacts();
  const { data: settings = {} } = useSettings();

  // XP multipliers from settings (with defaults)
  const xpRoleBase = settings.xpRoleBase ?? 50;
  const xpEngagement = settings.xpEngagement ?? 1;

  // Calculate XP breakdown for a single contact
  const getContactXP = useMemo(
    () => (contact) => {
      let roleXP = 0;
      let engagementXP = 0;
      let revenueXP = 0;

      // ── Role XP: crew contacts earn XP for jobs they appear in ──
      if (contact.type === 'crew') {
        const jobsWithContact = allJobs.filter((job) => {
          const ns = normalizeSnapshot(job.calcSnapshot);
          return getAllEarnings(ns).some((e) => e.name === contact.name);
        });
        roleXP = jobsWithContact.length * xpRoleBase;
      }

      // ── Engagement XP: all contacts earn from interactions ──
      const interactions = contact.data?.interactions || [];
      engagementXP = interactions.length * xpEngagement;

      // ── Client Revenue XP: clients earn from total revenue ──
      if (contact.type === 'client') {
        const clientJobs = allJobs.filter((j) => j.contactId === contact.id);
        const totalRevenue = clientJobs.reduce(
          (sum, j) => sum + (parseFloat(j.revenue) || 0),
          0
        );
        revenueXP = Math.floor(totalRevenue / 100);
      }

      const totalXP = roleXP + engagementXP + revenueXP;
      const tier = getTier(totalXP);

      return { totalXP, roleXP, engagementXP, revenueXP, tier };
    },
    [allJobs, xpRoleBase, xpEngagement]
  );

  // Build leaderboard: sorted array of all contacts with XP > 0
  const leaderboard = useMemo(() => {
    return allContacts
      .map((contact) => {
        const xpData = getContactXP(contact);
        return {
          contact,
          totalXP: xpData.totalXP,
          roleXP: xpData.roleXP,
          engagementXP: xpData.engagementXP,
          revenueXP: xpData.revenueXP,
          tier: xpData.tier,
        };
      })
      .filter((entry) => entry.totalXP > 0)
      .sort((a, b) => b.totalXP - a.totalXP);
  }, [allContacts, getContactXP]);

  return { getContactXP, leaderboard };
}
